import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, map, of, switchMap } from 'rxjs';
import { OnboardingFacade } from '../Facades/onboarding.facade';
import { Onboarding } from '../Models/onboarding.model';
import { OnboardingStatus } from '../Enums/onboarding-status.enum';

export const ONBOARDING_SESSION_RESOLVED_STATE = 'onboardingSessionResolved';

const COMPANY_SETUP_URL = '/setup/company-setup';
const PLAN_SELECTION_URL = '/setup/plan-selection';
const ADMIN_CONSENT_URL = '/setup/admin-consent';
const CONFIGURATION_CHOICE_URL = '/setup/configuration-choice';
const REVIEW_URL = '/setup/review';
const ALL_SET_URL = '/all-set';
const BLOCKED_URL = '/blocked';
const BLOCKED_COMPLETED_URL = '/blocked?reason=completed';
const BLOCKED_RENEWAL_URL = '/blocked?reason=renewal';

@Injectable({
  providedIn: 'root',
})
export class OnboardingResumeService {
  private readonly _onboardingFacade = inject(OnboardingFacade);

  resolveEntryUrl(): Observable<string> {
    return this._onboardingFacade.createSession().pipe(
      switchMap(onboarding => this.resolveForSession(onboarding)),
      catchError((error: unknown) => of(this.resolveForError(error))),
    );
  }

  private resolveForSession(onboarding: Onboarding): Observable<string> {
    if (onboarding.status === OnboardingStatus.COMPLETED) {
      return of(BLOCKED_COMPLETED_URL);
    }

    if (onboarding.isStatusAtLeast(OnboardingStatus.PAYMENT_CONFIRMED)) {
      return of(ALL_SET_URL);
    }

    if (!onboarding.isStatusAtLeast(OnboardingStatus.COMPANY_SAVED)) {
      return of(COMPANY_SETUP_URL);
    }

    if (!onboarding.hasSelectedPlan()) {
      return of(PLAN_SELECTION_URL);
    }

    if (onboarding.isStatusAtLeast(OnboardingStatus.PAYMENT_PENDING)) {
      return of(REVIEW_URL);
    }

    if (!onboarding.hasConsent()) {
      return of(ADMIN_CONSENT_URL);
    }

    return this.resolveBetweenConsentAndReview();
  }

  private resolveBetweenConsentAndReview(): Observable<string> {
    return this._onboardingFacade.refreshConfigurationChoice().pipe(
      map(applies => (applies ? CONFIGURATION_CHOICE_URL : REVIEW_URL)),
      catchError(() => of(REVIEW_URL)),
    );
  }

  private resolveForError(error: unknown): string {
    if (!(error instanceof HttpErrorResponse) || error.status !== 409) {
      return COMPANY_SETUP_URL;
    }

    const message = error.error?.message;

    if (message === 'onboarding.api.errors.onboarding.already_completed') {
      return BLOCKED_COMPLETED_URL;
    }

    return message === 'onboarding.api.errors.onboarding.renewal_not_permitted'
      ? BLOCKED_RENEWAL_URL
      : BLOCKED_URL;
  }
}
